import { Card, CardContent } from "../../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Users } from "lucide-react";

/**
 * Section D — Family Member Wise SIP Overview (Clubbed / Family View only).
 * Only renders when `familyAggregate && familyContext.is_group_head` and
 * the group has more than one family member.
 */
export const FamilySipOverviewCard = ({
  allTransactions = [],
  familyContext,
  familyAggregate,
  panToClientName = {},
}) => {
  if (!familyAggregate || !familyContext?.is_group_head) return null;
  const members = familyContext?.family_members || [];
  if (members.length <= 1) return null;

  // Bucket SIP purchases by `_family_pan` (stamped by the family-aggregate
  // backend on every merged transaction — server.py:6202).
  const byPan = {};
  (allTransactions || []).forEach((t) => {
    if ((t.reflecting_in || "") !== "Active Units") return;
    const desc = (t.description || "").toLowerCase();
    const flag = (t.flag || "").toLowerCase();
    const isSip = /\bsip\b/.test(desc) || /\bsystematic\b/.test(desc) || /\bsystematic\b/.test(flag);
    if (!isSip) return;
    const pan = (t._family_pan || t.pan || t.first_holder_pan || "").toUpperCase();
    if (!pan) return;
    if (!byPan[pan]) byPan[pan] = { count: 0, amount: 0 };
    byPan[pan].count += 1;
    byPan[pan].amount += Math.abs(parseFloat(t.amount) || 0);
  });

  const rows = members
    .map((m) => {
      const pan = (m.pan || "").toUpperCase();
      const b = byPan[pan] || { count: 0, amount: 0 };
      return { pan, name: panToClientName[pan] || m.name || pan, count: b.count, amount: b.amount };
    })
    .filter((r) => r.count > 0 || r.amount > 0);

  const totalCount = rows.reduce((s, r) => s + r.count, 0);
  const totalAmount = rows.reduce((s, r) => s + r.amount, 0);

  return (
    <Card className="border-gray-200" data-testid="summary-family-sip-card">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-teal-600" />
          FAMILY MEMBER WISE SIP OVERVIEW
        </h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Family member name</TableHead>
                <TableHead className="font-semibold text-right">Total SIP</TableHead>
                <TableHead className="font-semibold text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-gray-400 py-6">
                    No SIPs found across family members
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {rows.map((r) => (
                    <TableRow key={r.pan} className="hover:bg-gray-50" data-testid={`family-sip-row-${r.pan}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-right font-mono">{r.count}</TableCell>
                      <TableCell className="text-right font-mono">
                        ₹{r.amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-100 font-semibold border-t-2">
                    <TableCell className="font-bold">Total</TableCell>
                    <TableCell className="text-right font-mono font-bold">{totalCount}</TableCell>
                    <TableCell className="text-right font-mono font-bold">
                      ₹{totalAmount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
